import {
  AnalyticsEvent,
  BatchConfig,
  RetryEvent,
  UserActivityTrackCredentials,
  UserInformation,
} from "@plugins/user-activity-track/types.ts";
import { AWSFirehoseProvider } from "@plugins/user-activity-track/service/providers/firehose-client.ts";
import { AbstractMessageBatchProcessor } from "@plugins/user-activity-track/service/message.batch.processor.ts";
import {
  PROVIDER_TYPE,
  SCHEMA_VERSION,
} from "@plugins/user-activity-track/service/user.activity.track.constant.ts";
import { userActivityTrackCredential } from "@plugins/user-activity-track/service/user.activity.track.credential.ts";

class UserActivityTrack extends AbstractMessageBatchProcessor {
  private provider?: AWSFirehoseProvider;
  private userInformation?: UserInformation;
  private credentials?: UserActivityTrackCredentials;

  constructor() {
    super();
  }

  async initialize(
    type: string,
    userInformation: UserInformation,
    config: BatchConfig | null,
  ): Promise<void> {
    if (this.provider && this.userInformation && this.config) {
      return;
    }

    const fetchedCredentials = userActivityTrackCredential.getCredentials();
    if (
      !fetchedCredentials?.accessKeyId ||
      !fetchedCredentials?.accessKey ||
      !fetchedCredentials.sessionToken
    ) {
      const response = await userActivityTrackCredential.fetchUserActivityTrackCredentials();
      if (response) {
        this.setCredentials(userActivityTrackCredential.getCredentials());
      }
    }

    // initialize user information
    this.setUserInformation(userInformation);
    this.setBatchConfig(config);

    switch (type) {
      case PROVIDER_TYPE:
        this.provider = new AWSFirehoseProvider(this.credentials);
        break;
      default:
        throw new Error(`Unsupported provider: ${type}`);
    }
  }

  setUserInformation(userInformation: UserInformation): void {
    this.userInformation = userInformation;
  }

  setCredentials(credentials: UserActivityTrackCredentials): void {
    this.credentials = credentials;
  }

  async track(event: AnalyticsEvent): Promise<void> {
    const isLogoutEvent = event?.action === "logout";
    if (isLogoutEvent) {
      await this.addEvent(event, true);
    } else {
      await this.addEvent(event);
    }
  }

  async flush(flushAll): Promise<void> {
    if (!this.provider || !this.userInformation) return;

    if (this.eventQueue.length === 0) return;

    // check if credentials expired
    await userActivityTrackCredential.checkCredentialsExpiredState();
    const fetchedCredentials = userActivityTrackCredential.getCredentials();
    if (this.credentials.expiryDateTime !== fetchedCredentials.expiryDateTime) {
      // refresh credentials
      this.setCredentials(fetchedCredentials);
      // reinit firehose client
      this.reinitialize(this.credentials);
    }

    let sendEvent = this.eventQueue;
    if (flushAll) {
      this.eventQueue = [];
    } else {
      sendEvent = this.eventQueue.splice(0, this.config.batchSize);
    }

    const events = sendEvent.map((event) => {
      return {
        schemaVersion: SCHEMA_VERSION,
        ...this.userInformation,
        ...event,
      };
    }) as RetryEvent[];
    const retryableEvents = await this.provider.send(events);
    this.eventQueue.push(...retryableEvents);
  }

  reinitialize(credentials: UserActivityTrackCredentials) {
    console.log(`Reinitialize firehose client`);
    this.provider.initFirehoseClientAndCredentials(credentials);
  }
}

const userActivityTrack = new UserActivityTrack();
export default userActivityTrack;
